export default (methodologyId, autocompleteData) => ({
    value: null,
    showSuggestions: false,
    suggestions: [],
    selectedIndex: -1,
    cursorPosition: 0,
    autocompleteData: autocompleteData,
    isApplyingSuggestion: false,
    showFunctionTooltip: false,
    currentFunction: null,
    currentParameterIndex: 0,
    syncTimeout: null,

    init() {
        this.value = this.$wire.calculations[methodologyId] || '';

        // Focus the textarea initially
        this.$nextTick(() => {
            this.$refs.textarea.focus();
        });
    },

    onInput(event) {
        // Skip autocomplete if we're programmatically applying a suggestion
        if (this.isApplyingSuggestion) {
            return;
        }

        this.value = event.target.value;
        this.cursorPosition = event.target.selectionStart;

        // Sync back to Livewire lazily
        this.syncToLivewire();

        // Check for autocomplete trigger
        this.checkForAutocomplete();
        
        // Check for function tooltip
        this.checkForFunctionTooltip();
    },

    onKeyDown(event) {
        // Update cursor position for navigation keys
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
            this.$nextTick(() => {
                this.cursorPosition = this.$refs.textarea.selectionStart;
                this.checkForFunctionTooltip();
            });
        }

        if (!this.showSuggestions) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.selectedIndex = Math.min(this.selectedIndex + 1, this.suggestions.length - 1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
                break;
            case 'Enter':
            case 'Tab':
                if (this.selectedIndex >= 0) {
                    event.preventDefault();
                    this.applySuggestion(this.suggestions[this.selectedIndex]);
                }
                break;
            case 'Escape':
                this.hideSuggestions();
                break;
        }
    },

    checkForAutocomplete() {
        const text = this.value;
        const cursor = this.cursorPosition;

        // Find the word being typed at cursor position
        const beforeCursor = text.substring(0, cursor);
        const wordMatch = beforeCursor.match(/(\$?\w*)$/);

        if (wordMatch && wordMatch[1].length >= 1) {
            const partial = wordMatch[1].toLowerCase();
            const newSuggestions = this.getFilteredSuggestions(partial);

            // Clear first, then add new suggestions
            this.suggestions.splice(0, this.suggestions.length);
            if (newSuggestions.length > 0) {
                this.suggestions.push(...newSuggestions);
            }

            if (this.suggestions.length > 0) {
                this.selectedIndex = 0;
                this.showSuggestions = true;
                this.$nextTick(() => this.positionDropdown());
            } else {
                this.showSuggestions = false;
            }
        } else {
            this.showSuggestions = false;
            this.suggestions.splice(0, this.suggestions.length);
        }
    },

    getFilteredSuggestions(partial) {
        const suggestions = [];

        // BAV Functions
        if (this.autocompleteData.functions) {
            this.autocompleteData.functions.forEach(func => {
                if (func.name.toLowerCase().startsWith(partial.toLowerCase()) ||
                    func.name.toLowerCase().includes(partial.toLowerCase())) {
                    suggestions.push({
                        text: func.name,
                        display: func.name,
                        signature: func.signature || func.name,
                        description: func.description,
                        type: 'function'
                    });
                }
            });
        }

        // SQL Functions
        if (this.autocompleteData.sql_functions) {
            this.autocompleteData.sql_functions.forEach(func => {
                if (func.name.toLowerCase().startsWith(partial.toLowerCase()) ||
                    func.name.toLowerCase().includes(partial.toLowerCase())) {
                    suggestions.push({
                        text: func.name,
                        display: func.name,
                        signature: func.signature || func.name,
                        description: func.description,
                        type: 'sql_function'
                    });
                }
            });
        }

        // SQL Keywords
        if (this.autocompleteData.keywords) {
            this.autocompleteData.keywords.forEach(keyword => {
                if (keyword.toLowerCase().startsWith(partial.toLowerCase()) ||
                    keyword.toLowerCase().includes(partial.toLowerCase())) {
                    suggestions.push({
                        text: keyword,
                        display: keyword,
                        description: 'SQL Keyword',
                        type: 'keyword'
                    });
                }
            });
        }

        // Metrics (with $ prefix)
        if (this.autocompleteData.metrics) {
            const metricPartial = partial.startsWith('$') ? partial.substring(1) : partial;
            if (partial.startsWith('$') || metricPartial.length > 0) {
                this.autocompleteData.metrics.forEach(metric => {
                    const metricName = metric.name.toLowerCase();
                    if (metricName.startsWith(metricPartial.toLowerCase()) ||
                        metricName.includes(metricPartial.toLowerCase())) {
                        suggestions.push({
                            text: metric.name,
                            display: metric.name,
                            description: metric.description,
                            type: 'metric'
                        });
                    }
                });
            }
        }

        // Measures
        if (this.autocompleteData.measures) {
            this.autocompleteData.measures.forEach(measure => {
                if (measure.name.toLowerCase().startsWith(partial.toLowerCase()) ||
                    measure.name.toLowerCase().includes(partial.toLowerCase())) {
                    suggestions.push({
                        text: measure.name,
                        display: measure.name,
                        description: measure.description,
                        type: 'measure'
                    });
                }
            });
        }

        // Sort suggestions: exact matches first, then starts with, then contains
        suggestions.sort((a, b) => {
            const aExact = a.text.toLowerCase() === partial.toLowerCase();
            const bExact = b.text.toLowerCase() === partial.toLowerCase();
            const aStarts = a.text.toLowerCase().startsWith(partial.toLowerCase());
            const bStarts = b.text.toLowerCase().startsWith(partial.toLowerCase());

            if (aExact && !bExact) return -1;
            if (!aExact && bExact) return 1;
            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;
            return a.text.localeCompare(b.text);
        });

        return suggestions.slice(0, 10); // Limit to 10 suggestions
    },

    applySuggestion(suggestion) {
        this.isApplyingSuggestion = true;

        const textarea = this.$refs.textarea;
        const text = this.value;
        const cursor = this.cursorPosition;

        // Find the word being replaced
        const beforeCursor = text.substring(0, cursor);
        const wordMatch = beforeCursor.match(/(\$?\w*)$/);

        if (wordMatch) {
            const startPos = cursor - wordMatch[1].length;
            const newText = text.substring(0, startPos) +
                suggestion.text +
                text.substring(cursor);

            this.value = newText;

            // Sync the new value to Livewire immediately
            this.$wire.set(`calculations.${methodologyId}`, this.value);

            // Position cursor after inserted text
            const newCursorPos = startPos + suggestion.text.length;
            this.$nextTick(() => {
                textarea.focus();
                textarea.setSelectionRange(newCursorPos, newCursorPos);
                this.cursorPosition = newCursorPos;

                // Reset the flag after everything is done
                this.isApplyingSuggestion = false;
            });
        } else {
            this.isApplyingSuggestion = false;
        }

        this.hideSuggestions();
    },

    selectSuggestion(index) {
        if (index >= 0 && index < this.suggestions.length && this.suggestions[index]) {
            this.selectedIndex = index;
            this.applySuggestion(this.suggestions[index]);
        }
    },

    hideSuggestions() {
        this.showSuggestions = false;
        this.selectedIndex = -1;
    },

    positionDropdown() {
        // Simple positioning - just show below the textarea
        const dropdown = this.$refs.dropdown;
        if (dropdown) {
            dropdown.style.top = '100%';
            dropdown.style.left = '0';
        }
    },

    getSuggestionIcon(type) {
        switch (type) {
            case 'function':
                return '⚡';
            case 'sql_function':
                return '🔧';
            case 'keyword':
                return '🔤';
            case 'metric':
                return '📊';
            case 'measure':
                return '📏';
            default:
                return '•';
        }
    },

    checkForFunctionTooltip() {
        const text = this.value;
        const cursor = this.cursorPosition;
        
        // Find if cursor is inside a function call
        const beforeCursor = text.substring(0, cursor);
        
        // Look for function patterns: FUNCTION_NAME(
        const functionMatch = beforeCursor.match(/(\w+)\([^)]*$/);
        
        if (functionMatch) {
            const functionName = functionMatch[1].toUpperCase();
            
            // Check if it's a BAV function
            let foundFunction = this.autocompleteData.functions?.find(
                f => f.name === functionName
            );

            // If not found, check SQL functions
            if (!foundFunction) {
                foundFunction = this.autocompleteData.sql_functions?.find(
                    f => f.name === functionName
                );
            }
            
            if (foundFunction) {
                // Count commas to determine current parameter index
                const insideFunction = functionMatch[0].substring(functionMatch[1].length + 1);
                const parameterIndex = (insideFunction.match(/,/g) || []).length;
                
                this.currentFunction = foundFunction;
                this.currentParameterIndex = parameterIndex;
                this.showFunctionTooltip = true;
                return;
            }
        }
        
        // Hide tooltip if not inside a function
        this.showFunctionTooltip = false;
        this.currentFunction = null;
        this.currentParameterIndex = 0;
    },

    getParameterHighlight(signature) {
        if (!signature || this.currentParameterIndex < 0) return signature;
        
        // Extract parameters from signature: FUNC(param1, param2, param3)
        const match = signature.match(/\(([^)]+)\)/);
        if (!match) return signature;
        
        const parameters = match[1].split(',').map(p => p.trim());
        if (this.currentParameterIndex >= parameters.length) return signature;
        
        // Highlight current parameter with HTML
        const highlightedParams = parameters.map((param, index) => {
            return index === this.currentParameterIndex 
                ? `<span class="bg-blue-600 text-white px-1 rounded-md">${param}</span>` 
                : param;
        });
        
        return signature.replace(match[1], highlightedParams.join(', '));
    },

    syncToLivewire() {
        // Clear existing timeout
        if (this.syncTimeout) {
            clearTimeout(this.syncTimeout);
        }
        
        // Set new timeout for lazy syncing (500ms delay)
        this.syncTimeout = setTimeout(() => {
            this.$wire.set(`calculations.${methodologyId}`, this.value);
        }, 500);
    },

    syncToLivewireImmediate() {
        // Clear any pending lazy sync
        if (this.syncTimeout) {
            clearTimeout(this.syncTimeout);
            this.syncTimeout = null;
        }
        
        // Sync immediately
        this.$wire.set(`calculations.${methodologyId}`, this.value);
    }
});
